import { isDefined } from '@aloft/toolkit-shared';

import { Feature, FeatureCollection, Point } from 'geojson';
import mapboxgl from 'mapbox-gl';
import { useEffect, useRef } from 'react';

import { StatusFilterControl } from './StatusFilterControl.tsx';
import {
  MAP_LAYERS,
  MAP_LAYER_DEFINITIONS,
  MAP_PIN_STATUS_FILTERS,
  MAP_SOURCES,
} from './constants.ts';
import { Pin, PinStatusFilter, UseMapProps } from './types.ts';

const getStatusIcon = (status: PinStatusFilter) => {
  const key = status.toLocaleUpperCase() as keyof typeof MAP_PIN_STATUS_FILTERS;
  return key in MAP_PIN_STATUS_FILTERS ? key.toLocaleLowerCase() : 'expired';
};

export const propertyPinsToGeoJSON = (
  pins: Pin[],
): {
  type: 'geojson';
  data: FeatureCollection<Point>;
} => {
  const features: Feature<Point>[] = pins.map((pin) => {
    // Build icon name with suffixes
    let iconName = getStatusIcon(pin.icon);

    // Add valuation type suffix if present
    if (pin.valuationType === 'arv') {
      iconName += '-arv';
    } else if (pin.valuationType === 'aiv') {
      iconName += '-aiv';
    }
    // Add -selected suffix if active or selected
    else if (pin.active || pin.selected) {
      iconName += '-selected';
    }

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [pin.lng, pin.lat],
      },
      properties: {
        id: pin.id,
        icon: iconName,
        label: pin.label,
        status: getStatusIcon(pin.status),
        selected: pin.selected,
        active: pin.active,
        valuationType: pin.valuationType,
      },
    };
  });

  return {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features,
    },
  };
};

type Props<Feature> = {
  mapRef: UseMapProps<Feature>['mapRef'];
  pins: UseMapProps<Feature>['pins'];
  selectedStatuses?: UseMapProps<Feature>['selectedStatuses'];
  enableStatusFiltering?: UseMapProps<Feature>['enableStatusFiltering'];
  callbacks: {
    onPinFilterChange?: NonNullable<UseMapProps<Feature>['callbacks']>['onPinFilterChange'];
    onMapLoad: NonNullable<UseMapProps<Feature>['callbacks']>['onMapLoad'];
    onPinSourceLoad: NonNullable<UseMapProps<Feature>['callbacks']>['onPinSourceLoad'];
    onPinClick: NonNullable<UseMapProps<Feature>['callbacks']>['onPinClick'];
    onMoveEnd: NonNullable<UseMapProps<Feature>['callbacks']>['onMoveEnd'];
  };
};

export const usePinLayerCallbacks = <Feature>({
  mapRef,
  pins,
  selectedStatuses,
  enableStatusFiltering = false,
  callbacks,
}: Props<Feature>) => {
  const statusControlRef = useRef<StatusFilterControl | null>(null);

  const addStatusFilterControl = (map: mapboxgl.Map) => {
    if (!statusControlRef.current) {
      const statusControl = new StatusFilterControl({
        selected: selectedStatuses || [],
        onChange: callbacks.onPinFilterChange || (() => {}),
      });

      statusControlRef.current = statusControl;
      map.addControl(statusControl, 'bottom-left');
    } else {
      // Update props on existing control
      statusControlRef.current.updateProps({
        selected: selectedStatuses || [],
        onChange: callbacks.onPinFilterChange || (() => {}),
      });
    }
  };

  const handleOnLoadSource = (e: mapboxgl.MapboxEvent<undefined> & mapboxgl.EventData) => {
    const map = e.target;
    if (!isDefined(map)) return;
    callbacks.onMapLoad?.();

    if (!isDefined(pins)) return;
    if (!map.getSource(MAP_SOURCES.PINS)) {
      map.addSource(MAP_SOURCES.PINS, propertyPinsToGeoJSON(pins));
    }

    if (!map.getLayer(MAP_LAYERS.PINS)) {
      map.addLayer(MAP_LAYER_DEFINITIONS.PINS);
      // hack to ensure the subject layer is always on top
      if (map.getLayer(MAP_LAYER_DEFINITIONS.SUBJECT.id)) {
        map.moveLayer(MAP_LAYER_DEFINITIONS.SUBJECT.id);
      }
    }

    if (enableStatusFiltering) addStatusFilterControl(map);
  };

  const handleSourcedata = (e: mapboxgl.MapSourceDataEvent & mapboxgl.EventData) => {
    const { sourceId, isSourceLoaded, target } = e;
    if (sourceId === MAP_SOURCES.PINS && isSourceLoaded) {
      callbacks.onPinSourceLoad?.(MAP_SOURCES.PINS, target);
      if (enableStatusFiltering) addStatusFilterControl(target);
    }
  };

  const handlePinClick = (event: mapboxgl.MapLayerMouseEvent & mapboxgl.EventData) => {
    const feature = (event.features && event.features[0]) as unknown as Feature | undefined;
    callbacks.onPinClick?.(feature);
  };

  const handlePinMouseEnter = (event: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
    const map = event.target;
    map.getCanvas().style.cursor = 'pointer';
  };

  const handlePinMouseLeave = (event: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
    const map = event.target;
    map.getCanvas().style.cursor = 'grab';
  };

  const handleMoveEnd = (
    e: mapboxgl.MapboxEvent<MouseEvent | TouchEvent | WheelEvent | undefined> & mapboxgl.EventData,
  ) => {
    const map = e.target;
    if (!isDefined(map)) return;

    callbacks.onMoveEnd?.({
      center: map.getCenter(),
      zoom: map.getZoom(),
    });
  };

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!enableStatusFiltering || !map || !map.isStyleLoaded() || !map.getLayer(MAP_LAYERS.PINS)) {
      return;
    }

    map.setFilter(MAP_LAYERS.PINS, ['in', ['get', 'status'], ['literal', selectedStatuses]]);
  }, [mapRef, selectedStatuses, enableStatusFiltering]);

  // Update map source when pins change
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!isDefined(map) || !map.isStyleLoaded() || !isDefined(pins)) return;

    const source = map.getSource(MAP_SOURCES.PINS);
    if (source && source.type === 'geojson') {
      source.setData(propertyPinsToGeoJSON(pins).data);
    }
  }, [mapRef, pins]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!isDefined(map)) return;

    map.on('load', handleOnLoadSource);
    map.on('sourcedata', handleSourcedata);

    map.on('click', MAP_LAYERS.PINS, handlePinClick);
    map.on('mouseenter', MAP_LAYERS.PINS, handlePinMouseEnter);
    map.on('mouseleave', MAP_LAYERS.PINS, handlePinMouseLeave);
    map.on('moveend', handleMoveEnd);

    // eslint-disable-next-line
    return () => {
      map.off('load', handleOnLoadSource);
      map.off('sourcedata', handleSourcedata);
      map.off('click', MAP_LAYERS.PINS, handlePinClick);
      map.off('mouseenter', MAP_LAYERS.PINS, handlePinMouseEnter);
      map.off('mouseleave', MAP_LAYERS.PINS, handlePinMouseLeave);
      map.off('moveend', handleMoveEnd);
    };
  }, [callbacks, mapRef, selectedStatuses, statusControlRef]);
};
